import React, { useState, useEffect, ChangeEvent } from 'react';
import { fromUrl as geotiffFromUrl } from 'geotiff'; // Import geotiff.js
import proj4 from 'proj4';
import useGeoData from '../../../../store/useGeoData';
import type {
  GeoDataState,
  BBox,
  RasterSourceSpecification,
  RasterDemSourceSpecification,
} from '../../../../store/types';

import { ExtendedTextInput } from '../../../../common/inputs/TextInput/ExtendedTextInput';
import { NumberInput } from '../../../../common/inputs/NumberInput/NumberInput';
import UrlInput from '../../../../common/inputs/UrlInput/UrlInput';
import ToggleButton from '../../../../common/inputs/ToggleButton/ToggleButton';
import { SelectInput, type SelectOption } from '../../../../common/inputs/SelectInput/SelectInput';
import styles from './AddRasterLayerDialog.module.css';

interface AddRasterLayerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type UrlType = 'TileJSON' | 'TemplateXYZ' | 'COG URL' | 'OGC WMS Service';
type MapLibreSourceType = 'raster' | 'raster-dem';
type SchemeType = 'xyz' | 'tms';
type EncodingType = 'terrarium' | 'mapbox' | 'custom';

interface CommonTileProperties {
  bounds?: BBox;
  minzoom?: number;
  maxzoom?: number;
}

// Definizioni CRS di base che ci aspettiamo di usare.
// L'utente potrebbe dover aggiungere definizioni per CRS meno comuni se necessario,
// o potremmo espandere questo set.
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
proj4.defs("EPSG:3857", "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs");
// Aggiungiamo EPSG:32632 come esempio, ma idealmente non dovremmo hardcodare qui definizioni specifiche di zone UTM
// a meno che non siano estremamente comuni per gli utenti target o caricate dinamicamente.
proj4.defs("EPSG:32632", "+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs");


const AddRasterLayerDialog: React.FC<AddRasterLayerDialogProps> = ({ isOpen, onClose }) => {
  const [layerName, setLayerName] = useState('');
  const [tileUrlOrJsonUrl, setTileUrlOrJsonUrl] = useState('');
  const [urlType, setUrlType] = useState<UrlType>('TemplateXYZ');
  const [maplibreSourceType, setMaplibreSourceType] = useState<MapLibreSourceType>('raster');
  const [tileSize, setTileSize] = useState<number>(256);
  const [sourceAttribution, setSourceAttribution] = useState('');
  const [boundsStr, setBoundsStr] = useState('');
  const [scheme, setScheme] = useState<SchemeType>('xyz');
  const [isVolatile, setIsVolatile] = useState<boolean>(false);
  const [minZoomSource, setMinZoomSource] = useState<string>('');
  const [maxZoomSource, setMaxZoomSource] = useState<string>('');
  const [encoding, setEncoding] = useState<EncodingType>('mapbox');
  const [redFactor, setRedFactor] = useState<string>('1.0');
  const [greenFactor, setGreenFactor] = useState<string>('1.0');
  const [blueFactor, setBlueFactor] = useState<string>('1.0');
  const [baseShift, setBaseShift] = useState<string>('0.0');
  const [isLoadingBounds, setIsLoadingBounds] = useState(false); // Stato per caricamento bounds COG

  const addRasterSourceAndLayer = useGeoData((state: GeoDataState) => state.addRasterSourceAndLayer);
useEffect(() => {
    if (urlType === 'OGC WMS Service') {
      setScheme('tms');
    } else {
      // Se non è WMS, reimposta a 'xyz' come default generale per gli altri tipi che usano scheme (es. TemplateXYZ)
      // Questo evita che 'tms' rimanga selezionato se si passa da WMS a TemplateXYZ
      setScheme('xyz');
    }
  }, [urlType, setScheme]); // Aggiunto setScheme alle dipendenze come buona pratica, anche se non strettamente necessario qui

  useEffect(() => {
    if (isOpen) {
      setLayerName('');
      setIsLoadingBounds(false); // Resetta lo stato di caricamento
      setTileUrlOrJsonUrl('');
      setUrlType('TemplateXYZ');
      setMaplibreSourceType('raster');
      setTileSize(256);
      setSourceAttribution('');
      setBoundsStr('');
      setScheme('xyz');
      setIsVolatile(false);
      setMinZoomSource('');
      setMaxZoomSource('');
      setEncoding('mapbox');
      setRedFactor('1.0');
      setGreenFactor('1.0');
      setBlueFactor('1.0');
      setBaseShift('0.0');
    }
  }, [isOpen]);

  const handleSubmit = async () => { // Trasformato in async
    if (!layerName.trim() || !tileUrlOrJsonUrl.trim()) {
      alert('Layer name and source URL are required');
      return;
    }

    let parsedBounds: BBox | undefined = undefined;
    // Il parsing manuale dei bounds non si applica se estraiamo da COG o WMS (se boundsStr è vuoto)
    if (boundsStr.trim() && urlType !== 'COG URL' && !(urlType === 'OGC WMS Service' && !boundsStr.trim())) {
      const parts = boundsStr.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 4 && parts.every(p => !isNaN(p))) {
        parsedBounds = parts as BBox;
      } else {
        alert('Bounds format is invalid. Use minLng, minLat, maxLng, maxLat');
        return;
      }
    }

    const sourceMinZoomNum = minZoomSource.trim() ? parseInt(minZoomSource, 10) : undefined;
    const sourceMaxZoomNum = maxZoomSource.trim() ? parseInt(maxZoomSource, 10) : undefined;

    if (minZoomSource.trim() && isNaN(sourceMinZoomNum!)) {
        alert('Minzoom sorgente non valido.');
        return;
    }
    if (maxZoomSource.trim() && isNaN(sourceMaxZoomNum!)) {
        alert('Maxzoom sorgente non valido.');
        return;
    }

    let sourceSpec: RasterSourceSpecification | RasterDemSourceSpecification;

    const baseSourceProps = {
      attribution: sourceAttribution.trim() || undefined,
      volatile: isVolatile,
    };

    if (urlType === 'COG URL') {
      setIsLoadingBounds(true);
      let cogUrlForMaplibre = tileUrlOrJsonUrl.trim();
      if (maplibreSourceType === 'raster-dem' && !cogUrlForMaplibre.includes('#dem')) {
        cogUrlForMaplibre += '#dem';
      }

      try {
        const tiff = await geotiffFromUrl(tileUrlOrJsonUrl.trim());
        const image = await tiff.getImage();
        const cogBoundsRaw = image.getBoundingBox(); // [minX, minY, maxX, maxY]
        const geoKeys = image.getGeoKeys();
        let sourceCrsString: string | undefined = undefined;
        let needsTransformation = false;

        if (geoKeys) {
          if (geoKeys.GeographicTypeGeoKey && (geoKeys.GeographicTypeGeoKey === 4326 || geoKeys.GeographicTypeGeoKey === 4258)) {
            sourceCrsString = `EPSG:${geoKeys.GeographicTypeGeoKey}`;
            parsedBounds = cogBoundsRaw as BBox;
          } else if (geoKeys.ProjectedCSTypeGeoKey) {
            sourceCrsString = `EPSG:${geoKeys.ProjectedCSTypeGeoKey}`;
            needsTransformation = true;
          } else {
            parsedBounds = cogBoundsRaw as BBox; // Fallback
          }
        } else {
          parsedBounds = cogBoundsRaw as BBox; // Fallback
        }

        if (needsTransformation && sourceCrsString && cogBoundsRaw && cogBoundsRaw.length === 4 && cogBoundsRaw.every((b: unknown) => typeof b === 'number')) {
          try {
            proj4.defs(sourceCrsString);
            const targetCrs = "EPSG:4326";
            const [minLon, minLat] = proj4(sourceCrsString, targetCrs).forward([cogBoundsRaw[0], cogBoundsRaw[1]]);
            const [maxLon, maxLat] = proj4(sourceCrsString, targetCrs).forward([cogBoundsRaw[2], cogBoundsRaw[3]]);
            parsedBounds = [minLon, minLat, maxLon, maxLat];
          } catch (error) {
            if ((error as Error).message && (error as Error).message.includes("projection not named")) {
              alert(`Il CRS nativo del COG (${sourceCrsString}) non è definito. Impossibile trasformare i bounds. Il layer verrà aggiunto, ma lo zoom potrebbe non funzionare.`);
            } else {
              alert(`Errore durante la trasformazione dei bounds del COG. Il layer verrà aggiunto, ma lo zoom potrebbe non funzionare.`);
            }
            parsedBounds = undefined;
          }
        } else if (cogBoundsRaw && cogBoundsRaw.length === 4 && cogBoundsRaw.every((b: unknown) => typeof b === 'number') && !needsTransformation) {
           if (!parsedBounds) parsedBounds = cogBoundsRaw as BBox;
        } else if (!parsedBounds) {
            parsedBounds = undefined;
        }
      } catch (error) {
        alert(`Error processing COG: ${error instanceof Error ? error.message : String(error)}. The layer will be added without pre-fetched bounds.`);
      } finally {
        setIsLoadingBounds(false);
      }
      
      sourceSpec = {
        ...baseSourceProps,
        type: maplibreSourceType,
        url: `cog://${cogUrlForMaplibre}`,
        ...(parsedBounds && { bounds: parsedBounds }),
      };

    } else if (urlType === 'OGC WMS Service' && !boundsStr.trim()) {
      setIsLoadingBounds(true);
      try {
        let targetLayerName: string | undefined = undefined;
        const wmsBaseUrl = tileUrlOrJsonUrl.trim().split('?')[0];
        const getCapabilitiesUrl = `${wmsBaseUrl}?service=WMS&request=GetCapabilities&version=1.1.1`;

        try {
          const urlObj = new URL(tileUrlOrJsonUrl.trim());
          const layersParam = urlObj.searchParams.get('LAYERS') || urlObj.searchParams.get('layers');
          if (layersParam) {
            targetLayerName = layersParam.split(',')[0].trim(); // Prendi solo il primo layer se ce ne sono multipli
          }
        } catch (e) {
          console.warn("Could not parse WMS URL to extract layer name", e);
        }
        
        const response = await fetch(getCapabilitiesUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch GetCapabilities: ${response.statusText}`);
        }
        const xmlText = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");

        const errorNode = xmlDoc.getElementsByTagName('ServiceExceptionReport')[0] || xmlDoc.getElementsByTagName('ows:ExceptionReport')[0];
        if (errorNode) {
            const exceptionText = errorNode.getElementsByTagName('ServiceException')[0]?.textContent || errorNode.getElementsByTagName('ows:ExceptionText')[0]?.textContent;
            throw new Error(`WMS Service Error: ${exceptionText || 'Unknown error in GetCapabilities response'}`);
        }

        const capabilityNode = xmlDoc.getElementsByTagName('Capability')[0];
        if (!capabilityNode) {
            throw new Error('Invalid GetCapabilities XML: <Capability> tag not found.');
        }
        
        const layerNodes = capabilityNode.getElementsByTagName('Layer');
        let foundBounds: BBox | undefined = undefined;

        // Funzione helper per cercare LatLonBoundingBox
        const getLatLonBoundingBox = (layerElement: Element): BBox | undefined => {
          const latLonBox = layerElement.getElementsByTagName('LatLonBoundingBox')[0];
          if (latLonBox) {
            const minx = parseFloat(latLonBox.getAttribute('minx')!);
            const miny = parseFloat(latLonBox.getAttribute('miny')!);
            const maxx = parseFloat(latLonBox.getAttribute('maxx')!);
            const maxy = parseFloat(latLonBox.getAttribute('maxy')!);
            if (![minx, miny, maxx, maxy].some(isNaN)) {
              return [minx, miny, maxx, maxy];
            }
          }
          // Fallback per BoundingBox EPSG:4326
          const boundingBoxes = layerElement.getElementsByTagName('BoundingBox');
          for (let j = 0; j < boundingBoxes.length; j++) {
            const bbox = boundingBoxes[j];
            const crs = bbox.getAttribute('CRS') || bbox.getAttribute('SRS');
            if (crs === 'EPSG:4326') {
              const minx = parseFloat(bbox.getAttribute('minx')!);
              const miny = parseFloat(bbox.getAttribute('miny')!);
              const maxx = parseFloat(bbox.getAttribute('maxx')!);
              const maxy = parseFloat(bbox.getAttribute('maxy')!);
              if (![minx, miny, maxx, maxy].some(isNaN)) {
                return [minx, miny, maxx, maxy];
              }
            }
          }
          return undefined;
        };
        
        // 1. Cerca il layer radice (primo figlio Layer di Capability)
        let rootLayerBounds: BBox | undefined = undefined;
        const firstLayerDirectChildOfCapability = Array.from(capabilityNode.childNodes).find(node => node.nodeName === 'Layer') as Element | undefined;
        if (firstLayerDirectChildOfCapability) {
            rootLayerBounds = getLatLonBoundingBox(firstLayerDirectChildOfCapability);
        }


        for (let i = 0; i < layerNodes.length; i++) {
          const layerElement = layerNodes[i];
          let currentLayerName: string | null = null;
          const nameNode = Array.from(layerElement.childNodes).find(node => node.nodeName === 'Name');
          if (nameNode) {
            currentLayerName = nameNode.textContent;
          }

          if (targetLayerName && currentLayerName === targetLayerName) {
            foundBounds = getLatLonBoundingBox(layerElement);
            if (foundBounds) break;
          }
        }
        
        // Se il layer target non è stato trovato o non aveva bounds, prova con il rootLayerBounds
        if (!foundBounds && targetLayerName && rootLayerBounds) {
            console.warn(`Target layer "${targetLayerName}" found but without LatLonBoundingBox. Using root layer bounds as fallback.`);
            foundBounds = rootLayerBounds;
        }


        // Se nessun layer target specificato, o se il target non è stato trovato, usa i bounds del primo layer che li ha, o del layer radice
        if (!foundBounds && !targetLayerName) {
            if (rootLayerBounds) {
                foundBounds = rootLayerBounds;
            } else {
                 for (let i = 0; i < layerNodes.length; i++) {
                    // Considera solo i layer che sono figli diretti di un altro Layer o di Capability
                    // Questo evita di prendere bounds da layer annidati se non sono quelli che cerchiamo
                    if (layerNodes[i].parentNode && (layerNodes[i].parentNode!.nodeName === 'Layer' || layerNodes[i].parentNode!.nodeName === 'Capability')) {
                        foundBounds = getLatLonBoundingBox(layerNodes[i]);
                        if (foundBounds) break;
                    }
                }
            }
        }


        if (foundBounds) {
          parsedBounds = foundBounds;
          setBoundsStr(foundBounds.join(','));
        } else {
          console.warn('Could not automatically extract bounds from WMS GetCapabilities. Please enter them manually if needed.');
          alert('Impossibile recuperare automaticamente i bounds dal servizio WMS. Inseriscili manualmente se necessario.');
        }

      } catch (error) {
        console.error("Error fetching or parsing WMS GetCapabilities:", error);
        alert(`Impossibile recuperare automaticamente i bounds dal servizio WMS: ${error instanceof Error ? error.message : String(error)}. Inseriscili manualmente se necessario.`);
      } finally {
        setIsLoadingBounds(false);
      }
      // La sourceSpec verrà creata dopo, nella sezione 'else'
      const commonTileProps: CommonTileProperties = {
        bounds: parsedBounds,
        minzoom: sourceMinZoomNum,
        maxzoom: sourceMaxZoomNum,
      };
       if (maplibreSourceType === 'raster') {
        sourceSpec = {
          ...baseSourceProps,
          type: 'raster',
          tiles: [tileUrlOrJsonUrl.trim()],
          tileSize: tileSize,
          scheme: scheme,
          ...commonTileProps,
        };
      } else { // raster-dem
        const demSpec: RasterDemSourceSpecification = {
          ...baseSourceProps,
          type: 'raster-dem',
          tiles: [tileUrlOrJsonUrl.trim()],
          tileSize: tileSize,
          scheme: scheme,
          ...commonTileProps,
          encoding: encoding,
        };
         if (encoding === 'custom') {
          const parsedRed = parseFloat(redFactor);
          const parsedGreen = parseFloat(greenFactor);
          const parsedBlue = parseFloat(blueFactor);
          const parsedBase = parseFloat(baseShift);

          if (isNaN(parsedRed) || isNaN(parsedGreen) || isNaN(parsedBlue) || isNaN(parsedBase)) {
            alert('Valori per encoding custom non validi.');
            return;
          }
          demSpec.redFactor = parsedRed;
          demSpec.greenFactor = parsedGreen;
          demSpec.blueFactor = parsedBlue;
          demSpec.baseShift = parsedBase;
        }
        sourceSpec = demSpec;
      }

    } else { // TileJSON or TemplateXYZ (or WMS with manual bounds)
      const commonTileProps: CommonTileProperties = {
        bounds: parsedBounds, // This uses manually entered bounds if provided
        minzoom: sourceMinZoomNum,
        maxzoom: sourceMaxZoomNum,
      };

      if (maplibreSourceType === 'raster') {
        sourceSpec = {
          ...baseSourceProps,
          type: 'raster',
          ...commonTileProps,
        };
        if (urlType === 'TileJSON') {
          (sourceSpec as RasterSourceSpecification).url = tileUrlOrJsonUrl.trim();
        } else if (urlType === 'TemplateXYZ' || urlType === 'OGC WMS Service') { // WMS con bounds manuali usa questa logica
          (sourceSpec as RasterSourceSpecification).tiles = [tileUrlOrJsonUrl.trim()];
          (sourceSpec as RasterSourceSpecification).tileSize = tileSize;
          (sourceSpec as RasterSourceSpecification).scheme = scheme;
        }
      } else { // raster-dem
        const demSpec: RasterDemSourceSpecification = {
          ...baseSourceProps,
          type: 'raster-dem',
          ...commonTileProps,
          encoding: encoding,
        };
        if (urlType === 'TileJSON') {
          demSpec.url = tileUrlOrJsonUrl.trim();
        } else if (urlType === 'TemplateXYZ' || urlType === 'OGC WMS Service') { // WMS con bounds manuali usa questa logica
          demSpec.tiles = [tileUrlOrJsonUrl.trim()];
          demSpec.tileSize = tileSize;
          demSpec.scheme = scheme;
        }

        if (encoding === 'custom') {
          const parsedRed = parseFloat(redFactor);
          const parsedGreen = parseFloat(greenFactor);
          const parsedBlue = parseFloat(blueFactor);
          const parsedBase = parseFloat(baseShift);

          if (isNaN(parsedRed) || isNaN(parsedGreen) || isNaN(parsedBlue) || isNaN(parsedBase)) {
            alert('Valori per encoding custom non validi.');
            return;
          }
          demSpec.redFactor = parsedRed;
          demSpec.greenFactor = parsedGreen;
          demSpec.blueFactor = parsedBlue;
          demSpec.baseShift = parsedBase;
        }
        sourceSpec = demSpec;
      }
    }
    
    const idSuffix = urlType === 'COG URL' ? `cog-${maplibreSourceType}` : (urlType === 'OGC WMS Service' ? `wms-${maplibreSourceType}` : maplibreSourceType);
    const payloadForStore = {
      id: `${idSuffix}-${Date.now().toString()}`,
      name: layerName.trim(),
      sourceSpecification: sourceSpec,
    };

    try {
      addRasterSourceAndLayer(payloadForStore);
      onClose();
    } catch (error) {
      alert(`Errore durante l'aggiunta del layer: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  if (!isOpen) {
    return null;
  }

  const urlTypeOptions: SelectOption[] = [
    { value: 'TemplateXYZ', label: 'XYZ' },
    { value: 'TileJSON', label: 'TileJSON' },
    { value: 'OGC WMS Service', label: 'OGC WMS Service' },
    { value: 'COG URL', label: 'COG (Cloud Optimized GeoTIFF)' },
  ];

  const mapLibreSourceTypeOptions: SelectOption[] = [ 
    { value: 'raster', label: 'Raster' },
    { value: 'raster-dem', label: 'Raster DEM' },
  ];

  const schemeOptions: SelectOption[] = [
    { value: 'xyz', label: 'XYZ' },
    { value: 'tms', label: 'TMS' },
  ];

  const encodingOptions: SelectOption[] = [
    { value: 'mapbox', label: 'Mapbox' },
    { value: 'terrarium', label: 'Terrarium' },
    { value: 'custom', label: 'Custom' },
  ];

  const tileSizeOptions: SelectOption[] = [
    { value: 128, label: '128px' },
    { value: 256, label: '256px' },
    { value: 512, label: '512px' },
  ];

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: ChangeEvent<HTMLInputElement> | string) => {
    if (typeof event === 'string') {
      setter(event);
    } else {
      setter(event.target.value);
    }
  };

  const handleNumberInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (value: number | string | undefined) => {
    setter(String(value ?? ''));
  };
  
  const handleSelectChange = <T extends string>(setter: React.Dispatch<React.SetStateAction<T>>) => (event: ChangeEvent<HTMLSelectElement>) => {
    setter(event.target.value as T);
  };


  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 'var(--z-index-overlay)',
    }}>
      <div style={{
        backgroundColor: 'var(--color-background-ui)',
        padding: '20px',
        borderRadius: 'var(--border-radius-large, 8px)',
        boxShadow: 'var(--shadow-elevation-high, 0 8px 16px rgba(0,0,0,0.2))',
        width: 'clamp(400px, 50vw, 600px)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        color: 'var(--color-text-primary)',
        zIndex: 'var(--z-index-dialog)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xlarge, 1.5rem)', color: 'var(--color-text-primary)', flexGrow: 1, textAlign: 'center' }}>
            Add Raster Layer from URL
          </h2>
          <button
            onClick={onClose}
            aria-label="Chiudi dialogo"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: '1.8rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1',
            }}
          >
            &times;
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div style={{ flexGrow: 1, overflowY: 'auto', paddingRight: '10px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <ExtendedTextInput
            id="layerName"
            name="layerName"
            label="Layer Name:"
            value={layerName}
            onChange={handleInputChange(setLayerName)}
          />

          <div className={styles.gridContainer}> {/* Questo div conterrà solo i SelectInput */}
            <div className={styles.selectWrapper}>
              <label htmlFor="maplibreSourceType" className="input-label">MapLibre source type:</label>
              <SelectInput
                id="maplibreSourceType"
                options={mapLibreSourceTypeOptions}
                value={maplibreSourceType}
                onChange={handleSelectChange(setMaplibreSourceType)}
                className={styles.fullWidthSelect}
              />
            </div>
            <div className={styles.selectWrapper}>
              <label htmlFor="urlType" className="input-label">Source URL format:</label>
              <SelectInput
                id="urlType"
                options={urlTypeOptions}
                value={urlType}
                onChange={handleSelectChange(setUrlType)}
                className={styles.fullWidthSelect}
              />
            </div>
            {(urlType === 'TemplateXYZ' || urlType === 'COG URL' || urlType === 'OGC WMS Service') && (
              <div className={styles.selectWrapper}>
                <label htmlFor="tileSize" className="input-label">Tile Size (px):</label>
                <SelectInput
                  id="tileSize"
                  options={tileSizeOptions}
                  value={tileSize}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => setTileSize(Number(event.target.value))}
                  className={styles.fullWidthSelect}
                />
              </div>
            )}
          </div>
          {/* UrlInput spostato qui, come fratello di gridContainer */}
          <div className={styles.fullWidth}>
            <UrlInput
              id="tileUrlOrJsonUrl"
              label={
                urlType === 'COG URL' ? "COG URL (HTTP/HTTPS):" :
                urlType === 'OGC WMS Service' ? "WMS Service Base URL (con parametri GetMap):" :
                "Tiles URL:"
              }
              value={tileUrlOrJsonUrl}
              onChange={handleInputChange(setTileUrlOrJsonUrl)}
              placeholder={
                urlType === 'COG URL' ? "es. https://.../mio_file.tif" :
                urlType === 'OGC WMS Service' ? "es. http://example.com/wms?service=WMS&version=1.1.1&request=GetMap&layers=example&srs=EPSG:3857&format=image/png&width=256&height=256&bbox={bbox-epsg-3857}" :
                "es. https://.../{z}/{x}/{y}.png o /path/to/tile.json"
              }
            />
          </div>

          <ExtendedTextInput
            id="sourceAttribution"
            name="sourceAttribution"
            label="Attribution (Optional):"
            value={sourceAttribution}
            onChange={handleInputChange(setSourceAttribution)}
          />
          
          {/* Proprietà Sorgente Avanzate (senza titolo h3 e hr) */}
          {urlType !== 'COG URL' && (
            <>
              <div className={styles.advancedOptionsRow}>
                {/* Wrapper per Bounds Input */}
                <div className={styles.boundsInputWrapper}>
                  <ExtendedTextInput
                    id="boundsStr"
                    name="boundsStr"
                    label="Bounds (minLng,minLat,maxLng,maxLat):"
                    value={boundsStr}
                    onChange={handleInputChange(setBoundsStr)}
                    placeholder="-180,-85.0511,180,85.0511"
                  />
                </div>

                {/* Wrapper per MinZoom Input */}
                <div className={styles.minZoomInputWrapper}>
                  <label htmlFor="minZoomSource" className="input-label">MinZoom (Opt.):</label>
                  <NumberInput
                    id="minZoomSource"
                    value={minZoomSource}
                    onChange={handleNumberInputChange(setMinZoomSource)}
                    placeholder="0"
                    min={0}
                    max={24}
                    step={1}
                  />
                </div>

                {/* Wrapper per MaxZoom Input */}
                <div className={styles.maxZoomInputWrapper}>
                  <label htmlFor="maxZoomSource" className="input-label">MaxZoom (Opt.):</label>
                  <NumberInput
                    id="maxZoomSource"
                    value={maxZoomSource}
                    onChange={handleNumberInputChange(setMaxZoomSource)}
                    placeholder="22"
                    min={0}
                    max={24}
                    step={1}
                  />
                </div>
              </div>
              {(urlType === 'TemplateXYZ' || urlType === 'OGC WMS Service') && ( // Modificato qui
                <div className={styles.selectWrapper}> {/* Aggiunto marginTop per separazione */}
                  <label htmlFor="scheme" className="input-label">Scheme:</label>
                  <SelectInput
                    id="scheme"
                    options={schemeOptions}
                    value={scheme}
                    onChange={handleSelectChange(setScheme)}
                    className={styles.fullWidthSelect}
                  />
                </div>
              )}
            </>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs, 8px)', marginTop: '8px' }}>
            <ToggleButton
              id="isVolatileToggle"
              isOn={isVolatile}
              onToggle={() => setIsVolatile(!isVolatile)}
              label="Volatile Source (Optional):"
            />
          </div>

          {/* Configurazione Raster DEM (senza titolo h3 e hr) */}
          {maplibreSourceType === 'raster-dem' && urlType !== 'COG URL' && (
            <>
              <div className={styles.selectWrapper}>
                <label htmlFor="encoding" className="input-label">DEM Encoding:</label>
                <SelectInput
                  id="encoding"
                  options={encodingOptions}
                  value={encoding}
                  onChange={handleSelectChange(setEncoding)}
                  className={styles.fullWidthSelect}
                />
              </div>

              {encoding === 'custom' && (
                // Rimosso h4 "Parametri Encoding Custom:"
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px', border: '1px solid var(--color-border-ui-secondary)', padding: '12px', borderRadius: 'var(--border-radius-medium)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
                    <div>
                      <label htmlFor="redFactor" className="input-label">Red Factor:</label>
                      <NumberInput id="redFactor" value={Number(redFactor)} onChange={handleNumberInputChange(setRedFactor)} step="any" />
                    </div>
                    <div>
                      <label htmlFor="greenFactor" className="input-label">Green Factor:</label>
                      <NumberInput id="greenFactor" value={Number(greenFactor)} onChange={handleNumberInputChange(setGreenFactor)} step="any" />
                    </div>
                    <div>
                      <label htmlFor="blueFactor" className="input-label">Blue Factor:</label>
                      <NumberInput id="blueFactor" value={Number(blueFactor)} onChange={handleNumberInputChange(setBlueFactor)} step="any" />
                    </div>
                    <div>
                      <label htmlFor="baseShift" className="input-label">Base Shift:</label>
                      <NumberInput id="baseShift" value={Number(baseShift)} onChange={handleNumberInputChange(setBaseShift)} step="any" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', // Modificato padding
              cursor: 'pointer',
              backgroundColor: 'var(--color-button-secondary)',
              color: 'var(--color-text-button)',
              border: '1px solid var(--color-border-button-secondary)',
              borderRadius: 'var(--border-radius-medium)',
              fontSize: 'var(--font-size-normal)', // Mantenuto font-size per coerenza interna al dialogo se necessario
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoadingBounds} // Disabilita il pulsante durante il caricamento dei bounds
            style={{
              padding: '8px 16px',
              cursor: isLoadingBounds ? 'wait' : 'pointer',
              backgroundColor: isLoadingBounds ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: 'var(--font-size-normal)',
            }}
          >
            {isLoadingBounds ? (urlType === 'COG URL' ? 'Loading COG...' : 'Loading WMS...') : 'Load Raster'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRasterLayerDialog;